import React, {
  forwardRef,
  Ref,
  useState,
  useImperativeHandle,
  useEffect,
  useRef,
  ReactNode,
  JSX,
  RefObject,
  useCallback
} from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { toJS } from 'mobx';
import { parseInt } from 'lodash-es';
import { Alert, Col, ListGroup, ListGroupItem, Modal, ModalBody, Row, Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { faChevronRight, faUser, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ApiRoomContainer } from '@ibe/api';
import { useTheme } from '@emotion/react';
import { LoggerFactory } from '@ibe/services';
import { useDeepCompareEffect, useMount } from 'react-use';
import {
  IsoDropdown,
  MEDIAQUERY_DEFAULTS,
  PlusMinus,
  useConfig,
  useMediaQuery,
  useWindow
} from '@ibe/components';
import Keys from '../../Translations/generated/pax-room-selection.en.json.keys';
import PaxRoomSelectionController from './PaxRoomSelectionController';
import ChildAgePicker from './ChildAgePicker';
import { QueryUtils } from '../../Util/QueryUtils';
import useQuery from '../../Util/useQuery';

export type PaxRoomSelectionImperativeHandleRefType = Ref<{
  getTitle?: () => string | ReactNode;
  toggleDropdown?: () => void;
  toggleDropdownOnly?: (a: boolean) => void;
  getCurrentPax?: () => ApiRoomContainer[];
  validateChildrenSelection?: () => boolean;
}>;

export interface Props {
  initialOccupancy?: ApiRoomContainer[];
  value?: ApiRoomContainer[];
  minAdults?: number;
  maxAdults?: number;
  maxChildren?: number;
  maxChildAge?: number;
  maxCapacity?: number;
  maxRooms?: number;
  onChange: (o: ApiRoomContainer[]) => void;
  withRoomCountSelector?: boolean;
  customTitle?: string | ReactNode;
  childAgePlaceholder?: string;
  imperativeHandleRef?: PaxRoomSelectionImperativeHandleRefType;
  rightAlignedMenu?: boolean;
  onDropdownOpenChange?: (dropdownOpen: boolean) => void;
  fillWithValueOnOpen?: boolean;
  useInitialPersonAmounts?: boolean;
  skipSearch?: boolean;
  tabIndex?: number | null;
  accessibilityModal?: boolean; // Always true
  useCustomDropdownToggle?: boolean;
}

export const PaxRoomSelection = observer(
  forwardRef(function PaxRoomSelection(props: Props, ref: Ref<HTMLDivElement>): JSX.Element {
    const window = useWindow();
    const logger = LoggerFactory.get('PaxRoomSelection');
    const { t } = useTranslation('pax-room-selection');
    const onChange = props.onChange || ((p): void => logger.log('PaxRoomSelection.onChange', p));
    const state = useLocalObservable(() => new PaxRoomSelectionController());
    const stateRooms = state.rooms;
    const theme = useTheme();
    const config = useConfig();
    const query = useQuery();
    const hasOccupancyParam = QueryUtils.getOccupancy(query);

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const toggleButtonClicked = useRef<boolean>(false);
    const isMobile = useMediaQuery({ type: 'max', query: MEDIAQUERY_DEFAULTS.md });
    const [isScrollable, setIsScrollable] = useState<boolean>(false);
    const observerRef = useRef<ResizeObserver | null>(null);

    const scrollContainerRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }

        if (node !== null) {
          observerRef.current = new ResizeObserver(() => {
            const { scrollHeight, clientHeight } = node;
            setIsScrollable(scrollHeight > Math.ceil(clientHeight));
          });

          observerRef.current.observe(node);
        }
      },
      [state.allTravelersAmount]
    );

    const [initialPaxSelectionOverwritten, setInitialPaxSelectionOverwritten] = useState(false);

    const [windowSize, setWindowSize] = useState(getWindowSize());

    state.setMinAdults(props.minAdults || config.paxRoomConfig.minAdults || 1);
    state.setMaxAdults(
      (props.maxAdults || config.paxRoomConfig.maxAdults || state.defaultPersonsAmount) + 1
    );
    state.setMaxChildren(
      (props.maxChildren || config.paxRoomConfig.maxChildren || state.defaultPersonsAmount) + 1
    );
    state.setMaxCapacity(props.maxCapacity || config.paxRoomConfig.maxCapacity);
    state.setMaxRooms(props.maxRooms || config.paxRoomConfig.maxRooms);

    const withRoomCountSelector = props.withRoomCountSelector !== false;

    const [validAges, setValidAges] = useState(true);
    const initialRender = useRef<boolean>(false);
    const {
      customTitle,
      childAgePlaceholder,
      initialOccupancy,
      value,
      imperativeHandleRef,
      rightAlignedMenu,
      onDropdownOpenChange,
      fillWithValueOnOpen,
      useInitialPersonAmounts,
      skipSearch,
      accessibilityModal,
      useCustomDropdownToggle
    } = props;

    useMount(() => {
      initialRender.current = true;
    });

    useImperativeHandle(
      imperativeHandleRef,
      () => ({
        getTitle: () => paxTitle(),
        toggleDropdown,
        toggleDropdownOnly: (a: boolean) => setDropdownOpen(a),
        getCurrentPax: () => toJS(state.rooms),
        validateChildrenSelection
      }),
      [dropdownOpen, stateRooms, value]
    );

    const accessibilityModalRef = useRef<HTMLDivElement>(null);
    const [accessibilityModalOpen, setAccessibilityModalOpen] = useState(false);
    const icon = theme.paxSelectionOptions?.withIcon ? faUser : undefined;

    function withButton(): boolean {
      if (skipSearch) {
        return false;
      }
      if (config.components.paxRoomSelection) {
        if (windowSize.innerWidth > 768) {
          return !config.components.paxRoomSelection.disableButton;
        }
      }
      return true;
    }

    useDeepCompareEffect((): void => {
      if (initialPaxSelectionOverwritten) {
        return;
      }
      if (!!value && value.length > 0) {
        state.setRoomCount(value.length);
        state.setRooms([...value]);
        state.setInitialOccupancy({ ...value[0] });
      } else if (!!initialOccupancy && initialOccupancy.length > 0) {
        state.setRoomCount(initialOccupancy.length);
        state.setRooms([...initialOccupancy]);
        state.setInitialOccupancy(initialOccupancy[0]);
      } else {
        state.setRoomCount(1);
        state.triggerRoomCreation();
      }
    }, [value, state, initialOccupancy]);

    useEffect(() => {
      if (
        dropdownOpen &&
        initialRender.current &&
        !!value &&
        value.length > 0 &&
        fillWithValueOnOpen
      ) {
        state.setRoomCount(value.length);
        state.setRooms([...value]);
        validateChildrenSelection();
      }
    }, [fillWithValueOnOpen, dropdownOpen, initialRender]);

    useEffect(() => {
      function handleWindowResize() {
        setWindowSize(getWindowSize());
      }

      window?.addEventListener('resize', handleWindowResize);

      return () => {
        window?.removeEventListener('resize', handleWindowResize);
      };
    }, []);

    function getWindowSize() {
      const { innerWidth = 0, innerHeight = 0 } = window || {};
      return { innerWidth, innerHeight };
    }

    const validateChildrenSelection = (): boolean => {
      const agesAreValid = state.validateProvidedAges();
      setValidAges(agesAreValid);
      if (!agesAreValid) {
        return false;
      }
      return true;
    };

    const onClick = (): void => {
      if (!validateChildrenSelection()) {
        return;
      }

      setDropdownOpen(false);
      if (onDropdownOpenChange) {
        onDropdownOpenChange(false);
      }
      onChange(toJS(state.rooms));
      if (!initialPaxSelectionOverwritten) {
        setInitialPaxSelectionOverwritten(true);
      }
      if (accessibilityModal) {
        setAccessibilityModalOpen(false);
      }
    };

    const buildPaxTitle = (): string => {
      if (theme.paxSelectionOptions?.shortTitle) {
        return state.travelersAmount + (withRoomCountSelector ? `, ${state.roomAmount}` : '');
      }
      if (theme.paxSelectionOptions?.longTitle) {
        let roomTitle = '';
        if (withRoomCountSelector) {
          roomTitle = state.roomAmount + ' ' + t(Keys.room, { count: state.roomAmount }) + ', ';
        }

        return `${roomTitle}${state.numberOfAdults} ${t(Keys.adult, {
          count: state.numberOfAdults
        })}${
          state.numberOfChildren > 0
            ? `, ${state.numberOfChildren} ${t(Keys.child, { count: state.numberOfChildren })}`
            : ''
        }`;
      }
      return `${t(Keys.traveler, { count: state.travelersAmount })}: ${state.travelersAmount}${
        withRoomCountSelector
          ? `  -  ${t(Keys.room, { count: state.roomAmount })}: ${state.roomAmount}`
          : ''
      }`;
    };

    const maxRooms = props.maxRooms || config?.paxRoomConfig?.maxRooms;

    const buildPaxSelectionContent = (): JSX.Element => {
      return (
        <div className={'dropdown__wrapper'} ref={scrollContainerRef}>
          <button
            className="paxRoomSelection__mobile-close"
            onClick={() => {
              setDropdownOpen(false);
              if (onDropdownOpenChange) {
                onDropdownOpenChange(false);
              }
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <ListGroup flush className={'paxRoomSelection__plus-minus'}>
            {withRoomCountSelector && (
              <ListGroupItem
                className={`paxRoomSelection__rooms ${(!maxRooms || maxRooms === 1) && 'd-none'}`}
              >
                <>
                  {!!maxRooms && maxRooms > 1 && (
                    <Row>
                      <Col xs={12} sm={6}>
                        <PlusMinus
                          label={t(Keys.room, { count: 0 })}
                          currentCount={parseInt(state.roomCount?.value || '0', 10)}
                          onCountChange={(countValue: number): void =>
                            state.setRoomCountAndTriggerRoomCreation({
                              value: countValue.toString(),
                              label: countValue.toString()
                            })
                          }
                          minCount={1}
                          maxCount={state.maxRooms - 1}
                        />
                      </Col>
                    </Row>
                  )}
                </>
              </ListGroupItem>
            )}
            {state.rooms &&
              state.rooms.length > 0 &&
              state.rooms.map((room: ApiRoomContainer, i: number) => (
                <li key={i} className="paxRoomSelection__item ">
                  <ul>
                    <ListGroupItem>
                      <Row>
                        <Col>
                          {withRoomCountSelector && !!maxRooms && maxRooms > 1 && (
                            <p className="paxRoomSelection__heading">
                              {t(Keys.room)} {i + 1}
                            </p>
                          )}
                          <Row>
                            <Col xs={12} sm={6} className={'paxRoomSelection__item__person'}>
                              <PlusMinus
                                label={`${t(Keys.adult, { count: 2 })} ${t(Keys.adultAgeRange)}`}
                                currentCount={parseInt(
                                  state.getPersonCountOption(room.adults)?.value || '0',
                                  10
                                )}
                                onCountChange={(countValue: number): void =>
                                  state.setAdultForRoom(i, {
                                    value: countValue.toString(),
                                    label: countValue.toString()
                                  })
                                }
                                minCount={state.getMinAdults()}
                                maxCount={
                                  (useInitialPersonAmounts
                                    ? state.initialMaxAdults
                                    : state.getMaxAdults(i)) - 1
                                }
                                validationFailed={
                                  state.getAdultCountInRoom(i) >= state.getMaxAdultsLeftPerRoom(i)
                                }
                                occupancyErrorMessage={t(Keys.occupancyErrorMessage)}
                                plusButtonId={`plus-button-adult-${i}`}
                              />
                            </Col>
                            <Col
                              xs={12}
                              sm={6}
                              className="mt-3 mt-sm-0 paxRoomSelection__item__person"
                            >
                              <PlusMinus
                                label={`${t(Keys.child, { count: 2 })} ${t(Keys.childAgeRange)}`}
                                currentCount={parseInt(
                                  state.getPersonCountOption(room.children)?.value || '0',
                                  10
                                )}
                                onCountChange={(countValue: number): void =>
                                  state.setChildrenForRoom(i, {
                                    value: countValue.toString(),
                                    label: countValue.toString()
                                  })
                                }
                                minCount={0}
                                maxCount={
                                  (useInitialPersonAmounts
                                    ? state.initialMaxChildren
                                    : state.getMaxChildren(i)) - 1
                                }
                                plusButtonId={`plus-button-child-${i}`}
                                validationFailed={
                                  state.getChildrenCountInRoom(i) >=
                                  state.getMaxChildrenLeftPerRoom(i)
                                }
                                occupancyErrorMessage={t(Keys.occupancyErrorMessage)}
                              />
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </ListGroupItem>
                  </ul>
                  <ListGroupItem className="paxRoomSelection__child-age-picker__container">
                    <Row className="paxRoomSelection__child-age-picker">
                      {[...new Array(room.children).keys()].map(key => (
                        <Col
                          xs={12}
                          sm={6}
                          md={room.children <= 2 ? 6 : 3}
                          className="mt-2"
                          key={`${key}-col`}
                        >
                          <ChildAgePicker
                            key={key}
                            roomIndex={i}
                            childIndex={key}
                            maxChildAge={props.maxChildAge}
                            state={state}
                            childAgePlaceholder={childAgePlaceholder}
                            validateChildrenSelection={validateChildrenSelection}
                            paxHasError={!validAges}
                            newAgeSelect={isMobile}
                          />
                        </Col>
                      ))}
                    </Row>
                  </ListGroupItem>
                </li>
              ))}
            <ListGroupItem
              className={`paxRoomSelection__bottom ${isScrollable ? 'has-scroll-shadow' : ''}`}
            >
              {!validAges && <Alert color="danger">{t(Keys.validationErrorMessage)}</Alert>}
              {withButton() && (
                <Button tabIndex={0} color={'primary'} block onClick={onClick}>
                  <>
                    <span>{t(Keys.updateSearch)}</span>
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="ml-2 paxRoomSelection__confirm-button-icon"
                    />
                  </>
                </Button>
              )}
            </ListGroupItem>
          </ListGroup>
        </div>
      );
    };

    const paxTitle = (): string | ReactNode => {
      if (theme.paxSelectionOptions?.displayStaticTitleOnlyBeforeSelection) {
        if (hasOccupancyParam) {
          return buildPaxTitle();
        } else if (customTitle && !initialPaxSelectionOverwritten) {
          return customTitle;
        }
      } else if (customTitle) {
        return customTitle;
      }

      return buildPaxTitle();
    };

    const toggleDropdown = (): void => {
      if (!withButton() && dropdownOpen) {
        if (toggleButtonClicked.current || skipSearch) {
          toggleButtonClicked.current = false;
          onClick();
        }
      }
      setDropdownOpen(!dropdownOpen);
      if (onDropdownOpenChange) {
        onDropdownOpenChange(!dropdownOpen);
      }
    };

    const onToggleButtonClicked = (): void => {
      if (dropdownOpen) {
        toggleButtonClicked.current = true;
      }
    };

    return (
      <div
        className={`paxRoomSelection${
          !!state.rooms && state.rooms.length > 0 ? ' input--filled' : ''
        }`}
        ref={ref}
      >
        {accessibilityModal && (
          <div className="pax-room-selection__modal w-100" ref={accessibilityModalRef}>
            <Modal
              className="pax-room-selection__modal overflow-auto"
              isOpen={accessibilityModalOpen}
              toggle={() => setAccessibilityModalOpen(p => !p)}
              container={accessibilityModalRef as RefObject<HTMLElement>}
              unmountOnClose
            >
              <ModalBody>{buildPaxSelectionContent()}</ModalBody>
            </Modal>
            <Button
              outline
              className={'pax-room-selection__button sr-only sr-only-focusable'}
              onClick={() => setAccessibilityModalOpen(p => !p)}
            >
              <div className="d-flex">
                {icon && (
                  <div className="ml-auto">
                    <FontAwesomeIcon icon={icon} />
                  </div>
                )}
                <div className="w-100 text-truncate">{paxTitle() || ''}</div>
              </div>
            </Button>
          </div>
        )}
        {dropdownOpen && (
          <div
            className="paxRoomSelection__mobile-backdrop"
            onClick={e => {
              e.stopPropagation();
              toggleDropdown();
            }}
            onTouchStart={e => e.stopPropagation()}
          />
        )}
        <IsoDropdown
          isOpen={dropdownOpen}
          onDropdownToggle={toggleDropdown}
          icon={icon}
          title={paxTitle()}
          right={rightAlignedMenu}
          onToggleButtonClick={onToggleButtonClicked}
          tabIndex={props.tabIndex}
          closeCustomDropdownToggle={
            useCustomDropdownToggle
              ? () => {
                  setDropdownOpen(false);
                  if (onDropdownOpenChange) {
                    onDropdownOpenChange(false);
                  }
                }
              : undefined
          }
        >
          {buildPaxSelectionContent()}
        </IsoDropdown>
      </div>
    );
  })
);
export default PaxRoomSelection;
